import axios from 'axios';
import { ActionTree } from 'vuex';
import service from '../../services/entity.service';
import {
  mutationLoadingEntity,
  mutationSetEntitySettings,
  mutationSetUsers,
  mutationSetIsLoadingTeams,
  mutationSetPlanStatus,
  mutationSetEntity,
} from '../mutations/entity.mutations';
import { RootState } from '../store';
import { IEntityState } from '../stores/entity.store';
import { mutationSetTeams } from '../mutations/archive.mutations';
import { actionSetSettings } from './document-settings.actions';
import { translate } from '@/helpers/translation.helper';
import { IDocumentSettings } from '@/types/DocumentSettings';
import { IEntity } from '@/types/Entity';
import { IUser } from '@/types/User';
import { ITeam } from '@/types/Team';
import { IPlanStatus } from '@/types/plan-status';

export const actionLoadEntitySettings = 'loadEntitySettings';
export const actionChangePassword = 'changePassword';

export const actionLoadUsers = 'loadUsers';
export const actionLoadUsersSuccess = 'loadUsersSuccess';
export const actionLoadUsersFailed = 'loadUsersFailed';

export const actionLoadTeams = 'loadTeams';
export const actionLoadTeamsSuccess = 'loadTeamsSuccess';
export const actionLoadTeamsFailed = 'loadTeamsFailed';

export const actionSetIsLoadingTeams = 'setIsLoadingTeams';

export const actionLoadPlanStatus = 'loadPlanStatus';
export const actionLoadPlanStatusSuccess = 'loadPlanStatusSuccess';
export const actionLoadPlanStatusFailed = 'loadPlanStatusFailed';

export const actionLoadEntity = 'loadEntity';
export const actionLoadEntitySuccess = 'loadEntitySuccess';
export const actionLoadEntityFailed = 'loadEntityFailed';

export const actions: ActionTree<IEntityState, RootState> = {
  async [actionLoadEntitySettings]({ commit, dispatch }) {
    commit(mutationLoadingEntity, true);
    const {
      data: { entity },
    } = await axios.get(`/get/settings/entity`);
    if (entity) {
      const documentSettings: IDocumentSettings = {
        allowDocumentDownload: entity.document_download,
        allowSigningForward: entity.is_signing_forward,
        requireSelfSign: entity.is_selfsign,
        verifySmsOpen: entity.verify_sms_open,
        verifySmsSign: entity.verify_sms_sign,
        isSigningBiometric: entity.is_signing_biometric,
        isSigningInitials: entity.is_signing_initials,
        isSigningLinkedIn: entity.is_signing_linkedin,
        isSigningEId: entity.is_signing_eid,
        isSigningWrite: entity.is_signing_write,
        hasDisabledComments: entity.is_disable_comment,
        doIdentifyRecipient: entity.is_identify_recipient,
        hasEsignConsent: entity.is_esign_consent,
        documentUniqueId: entity.document_unique_id,
      };

      await dispatch(`documentSettings/${actionSetSettings}`, documentSettings, {
        root: true,
      });

      const entityData: IEntity = {
        entityId: entity.id,
        logoUrl: '',
        theme: null,
      };
      commit(mutationSetEntitySettings, entityData);
      commit(mutationLoadingEntity, false);
    }
  },
  async [actionLoadUsers]({ dispatch }) {
    try {
      const users: IUser[] = await service.fetchUsers();
      dispatch(actionLoadUsersSuccess, users);
      dispatch(actionLoadEntity);
    } catch (error) {
      dispatch(actionLoadUsersFailed, error);
    }
  },
  async [actionLoadUsersSuccess]({ commit }, users: IUser[]) {
    commit(mutationSetUsers, users);
  },
  async [actionLoadUsersFailed]({ dispatch }, error: any) {
    dispatch('alert/showAlert', { type: 'errorLoadUsers', payload: error }, { root: true });
  },

  async [actionLoadTeams]({ dispatch }) {
    dispatch(actionSetIsLoadingTeams, true);
    try {
      const teams: ITeam[] = await service.fetchTeams();
      dispatch(actionLoadTeamsSuccess, teams);
    } catch (e) {
      dispatch(actionLoadTeamsFailed);
    }
  },
  async [actionLoadTeamsSuccess]({ commit, dispatch }, teams: ITeam[]) {
    commit(mutationSetTeams, teams);
    dispatch(actionSetIsLoadingTeams, false);
  },
  async [actionLoadTeamsFailed]({ dispatch }) {
    dispatch(
      'alert/showDangerAlert',
      translate('could-not-load-teams', {
        root: true,
      })
    );
    dispatch(actionSetIsLoadingTeams, false);
  },
  async [actionSetIsLoadingTeams]({ commit }, payload: boolean) {
    commit(mutationSetIsLoadingTeams, payload);
  },
  async [actionLoadPlanStatus]({ dispatch }) {
    try {
      const planStatus: IPlanStatus = await service.fetchPlanStatus();
      dispatch(actionLoadPlanStatusSuccess, planStatus);
    } catch (error) {
      dispatch(actionLoadPlanStatusFailed);
    }
  },
  async [actionLoadPlanStatusSuccess]({ commit }, planStatus: IPlanStatus) {
    commit(mutationSetPlanStatus, planStatus);
  },
  async [actionLoadPlanStatusFailed]() {},
  async [actionLoadEntity]({ dispatch }) {
    try {
      const entity: IEntity = await service.fetchEntity();
      dispatch(actionLoadEntitySuccess, entity);
    } catch (e) {
      dispatch(actionLoadEntityFailed);
    }
  },
  [actionLoadEntitySuccess]({ commit }, entity: IEntity) {
    commit(mutationSetEntity, entity);
  },
  [actionLoadEntityFailed]({ dispatch }) {
    dispatch(
      'alert/showDangerAlert',
      translate('could-not-load-entity', {
        root: true,
      })
    );
  },
};
